import { BaseResponse } from '@/types/common';
import axiosInstance from './axiosInstance';

const baseUrl = `${SUPP_ADMIN_BASE_URL}/supp-management`;

/**
 * 投票活动列表查询参数
 */
export interface VoteContestListParams {
  /** ID */
  id?: number;
  /** 主标题 */
  title?: string;
  /** 创建人 */
  userName?: string;
  /** 活动状态；0：未开始；1：投稿中；2：用户评审中；3：已结束；4：运营颁奖 */
  status?: number;
  /** 当前页 */
  currentPage?: number;
  /** 每页数量 */
  pageSize?: number;
}

/**
 * 投票活动信息
 */
export interface VoteContest {
  /** ID */
  id?: number;
  /** 主标题 */
  title?: string;
  /** 副标题 */
  subtitle?: string;
  /** 图片 */
  contestImage?: string;
  /** 投稿开始时间 */
  submitBeginTime?: string;
  /** 投稿结束时间 */
  submitEndTime?: string;
  /** 投票开始时间 */
  voteBeginTime?: string;
  /** 投票结束时间 */
  voteEndTime?: string;
  /** 活动简介 */
  eventDescription?: string;
  /** 奖励人数 */
  prizeNum?: number;
  /** 每人可获得的奖励 */
  prizeQuota?: number;
  /** 规则类型；1：文字；2：图片；3：链接 */
  ruleType?: number;
  /** 规则描述 */
  ruleVal?: string;
  /** 投票次数权重 */
  voteWeight?: string;
  /** 点赞次数权重 */
  likeWeight?: string;
  /** 评论次数权重 */
  commentWeight?: string;
  /** 收藏次数权重 */
  collectWeight?: string;
  /** 被artlist引用次数权重 */
  artlistWeight?: string;
  /** 模型 */
  styleTypeId?: string;
  /** 画风ID */
  styleId?: string;
  /** 活动类型；1积分活动 2硬币活动 */
  contestType?: number;
  /** 是否为vip限定活动；0否 1是 */
  vipLimit?: number;
  /** 活动状态（接口返回，3 表示已结束） */
  status?: number;
}

/**
 * 投票活动列表响应
 */
export interface VoteContestListResponse {
  /** 总数 */
  total?: number;
  /** 当前页 */
  currentPage?: number;
  /** 每页数量 */
  pageSize?: number;
  /** 记录列表 */
  records?: VoteContest[];
}

/**
 * 投票作品列表查询参数
 */
export interface VoteArtworkListParams {
  /** 活动id */
  contestId?: string;
  /** 作品标题 */
  artworkTitle?: string;
  /** 用户名字 */
  userName?: string;
  /** 用户suppId */
  userSuppId?: string;
  /** 用户身份 vipName */
  vipName?: string;
  /** 充值订单平台：apple-苹果，android-安卓 */
  platform?: string;
  /** 是否获奖 true-已获奖，false-未获奖 */
  isAwarded?: boolean;
  /** 当前页 */
  currentPage?: number;
  /** 每页数量 */
  pageSize?: number;
}

/**
 * 投票作品信息
 */
export interface VoteArtwork {
  /** 作品ID */
  artworkId?: string;
  /** 作品图片url */
  artworkImageUrl?: string;
  /** 作品标题 */
  artworkTitle?: string;
  /** 热度 */
  hotScore?: number;
  /** 票数 */
  voteNum?: number;
  /** 点赞数 */
  likeNum?: number;
  /** 收藏数 */
  collectNum?: number;
  /** 评论数 */
  commentNum?: number;
  /** 是否获奖 true-已获奖，false-未获奖 */
  isAwarded?: boolean;
  /** 用户名 */
  userName?: string;
  /** 用户suppId */
  userSuppId?: string;
  /** 用户身份 vipName */
  vipName?: string;
}

/**
 * 投票作品列表响应
 */
export interface VoteArtworkListResponse {
  /** 总数 */
  total?: number;
  /** 当前页 */
  currentPage?: number;
  /** 每页数量 */
  pageSize?: number;
  /** 记录列表 */
  records?: VoteArtwork[];
}

/**
 * API响应格式
 */
export interface ApiResponse<T> {
  /** 状态码 */
  status?: number;
  /** 是否成功 */
  success?: boolean;
  /** 消息 */
  message?: string;
  /** 数据 */
  data?: T;
}

/**
 * 获奖信息响应
 */
export interface AwardInfo {
  /** 活动ID */
  contestId: string;
  /** 获奖人数 */
  awardedNum: number;
  /** 总人数 */
  total: number;
}


/**
 * 获取投票活动列表
 * @param params - 查询参数
 * @returns Promise<ApiResponse<VoteContestListResponse>>
 */
export const getVoteContestList = async (params: VoteContestListParams) => {
  const res = await axiosInstance.post<ApiResponse<VoteContestListResponse>>(
    `${baseUrl}/contest/v1/list?pageSize=${params.pageSize}&currentPage=${params.currentPage}`,
    params
  );
  return res.data;
};

/**
 * 获取投票活动详情
 * @param params - 查询参数
 * @returns Promise<ApiResponse<VoteContest>>
 */
export const getVoteContestDetail = async (params: { id: number }) => {
  const res = await axiosInstance.get<ApiResponse<VoteContest>>(
    `${baseUrl}/contest/v1/detail/${params.id}`
  );
  return res.data;
};

/**
 * 分页获取活动的投票作品
 * @param params - 查询参数
 * @returns Promise<ApiResponse<VoteArtworkListResponse>>
 */
export const getVoteArtworkList = async (params: VoteArtworkListParams) => {
  const res = await axiosInstance.post<ApiResponse<VoteArtworkListResponse>>(
    `${baseUrl}/contest/v1/artworkPageList`,
    params
  );
  return res.data;
};

/**
 * 获取活动获奖信息
 * @param contestId - 活动ID
 * @returns Promise<ApiResponse<AwardInfo>>
 */
export const getAwardInfo = async (
  contestId: string
): Promise<ApiResponse<AwardInfo>> => {
  const res = await axiosInstance.get(`${baseUrl}/contest/v1/awardInfo`, {
    params: { contestId },
  });

  return res.data;
};

/**
 * 提交评比结果
 * @param contestId - 活动ID
 * @returns Promise<ApiResponse<void>>
 */
export const submitAwardResult = async (
  contestId: string
): Promise<ApiResponse<void>> => {
  const res = await axiosInstance.post(`${baseUrl}/contest/v1/award/submit`, {
    contestId,
  });
  return res.data;
};

/**
 * 创建投票活动参数
 */
export interface CreateVoteContestParams {
  /** 主标题 */
  title: string;
  /** 副标题 */
  subtitle: string;
  /** 图片 */
  contestImage: string;
  /** 投稿开始时间 */
  submitBeginTime: string;
  /** 投稿结束时间 */
  submitEndTime: string;
  /** 奖励人数 */
  prizeNum: number;
  /** 每人可获得的奖励 */
  prizeQuota: number;
  /** 规则类型；1：文字；2：图片；3：链接 */
  ruleType: number;
  /** 规则描述 */
  ruleVal: string;
  /** 投票次数权重 */
  voteWeight: string;
  /** 点赞次数权重 */
  likeWeight: string;
  /** 评论次数权重 */
  commentWeight: string;
  /** 收藏次数权重 */
  collectWeight: string;
  /** 被artlist引用次数权重 */
  artlistWeight: string;
}

/**
 * 创建投票活动（包含Excel文件上传）
 * @param contestDTO - 投票活动参数
 * @param file - 要上传的文件
 * @returns Promise<ApiResponse<void>>
 */
export const createVoteContest = async (
  contestDTO: CreateVoteContestParams,
  file: File
): Promise<ApiResponse<void>> => {
  const formData = new FormData();
  formData.append('contestDTO', JSON.stringify(contestDTO));
  formData.append('file', file);
  const res = await axiosInstance.post<ApiResponse<void>>(
    `${baseUrl}/contest/v1/addContest`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return res.data;
};

/**
 * 创建活动的接口函数
 * @param params - 创建参数
 * @returns Promise<ApiResponse<void>>
 */
export const createActivity = async (params: CreateVoteContestParams) => {
  const res = await axiosInstance.post<ApiResponse<void>>(
    `${baseUrl}/activity/v1/create`,
    params
  );
  return res.data;
};

/**
 * 更新投票活动参数
 */
export interface UpdateVoteContestParams extends CreateVoteContestParams {
  id: number;
}

/**
 * 更新投票活动
 * @param params - 更新参数
 * @returns Promise<ApiResponse<void>>
 */
export const updateVoteContest = async (
  contestDTO: CreateVoteContestParams,
  file: File
): Promise<ApiResponse<void>> => {
  const formData = new FormData();
  formData.append('contestDTO', JSON.stringify(contestDTO));
  formData.append('file', file);
  const res = await axiosInstance.post<ApiResponse<void>>(
    `${baseUrl}/contest/v1/updateContest`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return res.data;
};

/**
 * 删除投票活动
 * @param id - 活动ID
 * @returns Promise<ApiResponse<void>>
 */
export const deleteVoteContest = async (id: number) => {
  const res = await axiosInstance.delete<BaseResponse<void>>(
    `${baseUrl}/contest/v1/${id}`
  );
  return res.data;
};

/**
 * 批量设置获奖作品
 * @param params - 设置参数
 * @returns Promise<ApiResponse<void>>
 */
export const setArtworkAward = async (params: {
  artworkIds: string[];
  contestId: string;
}): Promise<ApiResponse<void>> => {
  const res = await axiosInstance.post(
    `${baseUrl}/contest/v1/award/set`,
    params
  );
  return res.data;
};

/**
 * 批量取消获奖作品
 * @param params - 取消参数
 * @returns Promise<ApiResponse<void>>
 */
export const cancelArtworkAward = async (params: {
  artworkIds: string[];
  contestId: string;
}): Promise<ApiResponse<void>> => {
  const res = await axiosInstance.post(
    `${baseUrl}/contest/v1/award/cancel`,
    params
  );
  return res.data;
};

interface ContestNoticeDTO {
  id: number;
  content: string;
  linkType: number;
  link: string;
  publishStatus: number;
  createTime: string;
  createUserName: string;
}

interface ResponseDTO {
  status: number;
  success: boolean;
  message: string;
  data: object;
}

interface QueryContestNoticeDTO {
  content: string;
  linkType: number;
  link: string;
}

interface ResponseDTOPageQueryResultContestNoticeDTO {
  status: number;
  success: boolean;
  message: string;
  data: PageQueryResultContestNoticeDTO;
}

interface PageQueryResultContestNoticeDTO {
  total: number;
  currentPage: number;
  pageSize: number;
  records: ContestNoticeDTO[];
}

/**
 * 保存比赛活动公告
 * @param {*} params
 * @returns
 */
export const saveNotice = async (
  params: ContestNoticeDTO
): Promise<ApiResponse<ResponseDTO>> => {
  const response = await axiosInstance.post(
    `${baseUrl}/contest/notice/v1/save`,
    params
  );

  return response;
};

/**
 * 发布比赛活动公告
 * @param {*} params
 * @returns
 */
export const publishNotice = async (params: {
  id: number;
  publishStatus: number;
}): Promise<ApiResponse<ResponseDTO>> => {
  const response = await axiosInstance.post(
    `${baseUrl}/contest/notice/v1/publish`,
    params
  );

  return response;
};

/**
 * 查询比赛活动公告
 * @param {*} params
 * @returns
 */
export const getNoticeList = async (
  params: QueryContestNoticeDTO & {
    currentPage: number;
    pageSize: number;
  }
): Promise<ApiResponse<ResponseDTOPageQueryResultContestNoticeDTO>> => {
  const response = await axiosInstance.post(
    `${baseUrl}/contest/notice/v1/list?currentPage=${params.currentPage}&pageSize=${params.pageSize}`,
    params
  );

  return response;
};

/**
 * 获取比赛活动公告详情
 * @param {*} params
 * @returns
 */
export const getNoticeDetail = async (params: {
  id: number;
}): Promise<ApiResponse<ResponseDTO>> => {
  const response = await axiosInstance.post(
    `${baseUrl}/contest/notice/v1/detail/${params.id}`,
    params
  );

  return response;
};

/**
 * 删除比赛活动公告
 * @param {*} params
 * @returns
 */
export const deleteNotice = async (params: {
  id: number;
}): Promise<ApiResponse<ResponseDTO>> => {
  const response = await axiosInstance.post(
    `${baseUrl}/contest/notice/v1/delete/${params.id}`,
    params
  );

  return response;
};

/**
 * 获取模型选择列表
 * @returns Promise<ApiResponse<any>>
 */
export const getModelList = async (): Promise<ApiResponse<ResponseDTO>> => {
  const res = await axiosInstance.get<ApiResponse<ResponseDTO>>(
    // `${baseUrl}/contest/selectModel`
    `${baseUrl}/artworkAiStyleType/styleTypeAll`
  );
  return res.data;
};

/**
 * 获取画风选择列表
 * @param params - 包含模型和关键字
 * @returns Promise<ApiResponse<any>>
 */
export const getArtStyleList = async (
  params?: string
): Promise<ApiResponse<ResponseDTO>> => {
  const res = await axiosInstance.get<ApiResponse<ResponseDTO>>(
    // `${baseUrl}/contest/selectArtStyle`,
    `${baseUrl}/artworkAiStyleType/styleTypeById?styleTypeId=${params}`
  );
  return res.data;
};
